import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set axios default header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [token]);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Auth check failed:', error);
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [token]);

  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [requires2FAVerify, setRequires2FAVerify] = useState(false);
  const [pendingLoginCreds, setPendingLoginCreds] = useState(null);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const data = response.data;
    
    // 2FA verification needed (already set up)
    if (data.requires_2fa_verify) {
      setPendingLoginCreds({ email, password });
      setRequires2FAVerify(true);
      return data;
    }
    
    // Login success but 2FA setup needed (mandatory)
    setToken(data.access_token);
    setUser(data.user);
    if (data.requires_2fa_setup) {
      setRequires2FASetup(true);
    }
    return data;
  };

  const verify2FA = async (code) => {
    if (!pendingLoginCreds) throw new Error('No pending login');
    const response = await axios.post(`${API}/auth/2fa/verify`, {
      email: pendingLoginCreds.email,
      password: pendingLoginCreds.password,
      totp_code: code
    });
    setToken(response.data.access_token);
    setUser(response.data.user);
    setRequires2FAVerify(false);
    setPendingLoginCreds(null);
    return response.data;
  };

  const setup2FA = async () => {
    const response = await axios.post(`${API}/auth/2fa/setup`);
    return response.data; // { secret, qr_code, provisioning_uri }
  };

  const confirm2FA = async (code) => {
    const response = await axios.post(`${API}/auth/2fa/confirm`, { code });
    setRequires2FASetup(false);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    setToken(response.data.access_token);
    setUser(response.data.user);
    if (response.data.requires_2fa_setup) {
      setRequires2FASetup(true);
    }
    return response.data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setRequires2FASetup(false);
    setRequires2FAVerify(false);
    setPendingLoginCreds(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, 
      login, register, logout,
      requires2FASetup, requires2FAVerify,
      verify2FA, setup2FA, confirm2FA
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
